import { imageUrlKey } from 'constants/rulebookVariables';

/** The market fields a market's own image is read from. */
export interface MarketImageFields {
    /** The canonical market image column (Kalshi/exchange-infra#30177). */
    image_url?: string | null;
    rulebook_variables?: Record<string, string> | null;
}

/**
 * `raw` trimmed when it is an absolute http(s) URL with a host, else null. The
 * market image column historically holds placeholders such as "a" and
 * "fake_image.com", which must read as no image.
 */
export function validMarketImageUrl(raw: string | null | undefined): string | null {
    const trimmed = raw?.trim();
    // WHATWG URL parsing is laxer than the server's Go url.Parse ("https:host",
    // "https:///host" both gain a host), so require the authority up front.
    if (!trimmed || !/^https?:\/\/[^/?#]/i.test(trimmed)) return null;
    try {
        return new URL(trimmed).host ? trimmed : null;
    } catch {
        return null;
    }
}

/**
 * A market's own image, before any CDN sizing: its `image_url` when that is a
 * real http(s) URL, else the legacy `rulebook_variables.image_link`, else null.
 * Mirrors exchange-infra's `unimodel.CanonicalMarketImage`, so it reads the
 * same image before and after the server starts serving the column. Never
 * reads the market's `product_metadata`.
 */
export function marketImageSource(market: MarketImageFields | null | undefined): string | null {
    if (!market) return null;
    return validMarketImageUrl(market.image_url) ?? validMarketImageUrl(market.rulebook_variables?.[imageUrlKey]);
}
